import type { PropertyImage } from '@/core/services/contentfulClient';
import { ADHA_UNITS_GRID_GAP } from '@/modules/properties-web/list/adhaListingLayout';
import {
  createContext,
  startTransition,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  readListingUnitModel,
  resolveListingCardImages,
} from '../utils/pickMixedUnitImages';

/** Used until the units ScrollView reports its height. */
const FALLBACK_VIEWPORT_HEIGHT = 720;
/** Stand-in row height until the first visible card is measured. */
const ESTIMATED_CARD_HEIGHT = 400;
/** Extra rows mapped just outside the pane so the next scroll is already ready. */
const OVERSCAN_ROWS = 1;
const CARD_HEIGHT_EPSILON = 8;
/**
 * One scroll event (throttled to ~16ms) moving more than this share of a card
 * counts as fast. While fast, nothing renders so the browser can paint skeletons.
 */
const FAST_SCROLL_CARD_FRACTION = 0.5;
/** Quiet time after the last scroll event before the pane counts as stopped. */
const FLING_SETTLE_MS = 100;
/** Slots are rendered in memoized blocks of this size so an appended page only mounts new blocks. */
export const LISTING_SLOT_BLOCK_SIZE = 50;
/** No scroll, pointer, key or touch input for this long counts as idle. */
const IDLE_BEFORE_BACKGROUND_MS = 5_000;
/** Cards mapped per background step while idle, top of the list first. */
const BACKGROUND_BATCH_SIZE = 3;
const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'] as const;

const EMPTY_IMAGES: Record<string, { uri: string }[]> = {};

export interface ListingViewportSnapshot {
  readonly scrollY: number;
  readonly viewportHeight: number;
  readonly cardHeight: number;
}

export interface ListingViewportStore {
  readonly subscribe: (listener: () => void) => () => void;
  readonly getSnapshot: () => ListingViewportSnapshot;
  readonly getRawScrollY: () => number;
  readonly update: (scrollY: number, viewportHeight: number) => void;
  readonly setCardHeight: (height: number) => void;
  /** True once the pane has stopped moving; cards then render at once, not in the background. */
  readonly isStopped: () => boolean;
}

/** The store whose pane scrolled last; the browser's `scrollend` settles it immediately. */
let lastScrolledStore: { settleNow: () => void } | null = null;
let scrollEndListenerInstalled = false;

let lastUserActivityAt = Date.now();
let activityListenersInstalled = false;

function noteUserActivity(): void {
  lastUserActivityAt = Date.now();
}

function installActivityListeners(): void {
  if (activityListenersInstalled || typeof document === 'undefined') {
    return;
  }
  activityListenersInstalled = true;
  noteUserActivity();
  for (const type of ACTIVITY_EVENTS) {
    document.addEventListener(type, noteUserActivity, { capture: true, passive: true });
  }
}

function msUntilIdle(): number {
  return Math.max(0, IDLE_BEFORE_BACKGROUND_MS - (Date.now() - lastUserActivityAt));
}

function installScrollEndListener(): void {
  if (scrollEndListenerInstalled || typeof document === 'undefined') {
    return;
  }
  scrollEndListenerInstalled = true;
  // `scrollend` does not bubble; capture sees it for the units pane's own scroller.
  document.addEventListener(
    'scrollend',
    () => {
      lastScrolledStore?.settleNow();
    },
    { capture: true, passive: true },
  );
}

const INITIAL_SNAPSHOT: ListingViewportSnapshot = {
  scrollY: 0,
  viewportHeight: 0,
  cardHeight: ESTIMATED_CARD_HEIGHT,
};

function quantize(value: number, stride: number): number {
  if (stride <= 0) {
    return value;
  }
  return Math.floor(value / stride) * stride;
}

export function createListingViewportStore(): ListingViewportStore {
  let rawScrollY = 0;
  let rawViewportHeight = 0;
  let cardHeight = ESTIMATED_CARD_HEIGHT;
  let snapshot = INITIAL_SNAPSHOT;
  let flinging = false;
  let stopped = true;
  let settleTimer: ReturnType<typeof setTimeout> | null = null;
  const listeners = new Set<() => void>();
  const notify = () => listeners.forEach((listener) => listener());

  const settle = () => {
    if (settleTimer != null) {
      clearTimeout(settleTimer);
      settleTimer = null;
    }
    if (stopped) {
      return;
    }
    flinging = false;
    stopped = true;
    if (!publish()) {
      // Same rows as the last (background) update: re-send so it renders now.
      notify();
    }
  };

  const publish = (): boolean => {
    const stride = cardHeight + ADHA_UNITS_GRID_GAP;
    const scrollY = quantize(rawScrollY, stride);
    const viewportHeight =
      rawViewportHeight > 0
        ? Math.ceil(rawViewportHeight / stride) * stride
        : 0;
    if (
      snapshot.scrollY === scrollY &&
      snapshot.viewportHeight === viewportHeight &&
      snapshot.cardHeight === cardHeight
    ) {
      return false;
    }
    snapshot = { scrollY, viewportHeight, cardHeight };
    notify();
    return true;
  };

  const store = { settleNow: settle };

  return {
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getSnapshot: () => snapshot,
    getRawScrollY: () => rawScrollY,
    isStopped: () => stopped,
    update: (scrollY, viewportHeight) => {
      installScrollEndListener();
      lastScrolledStore = store;
      const jump = Math.abs(scrollY - rawScrollY);
      rawScrollY = scrollY;
      rawViewportHeight = viewportHeight;
      if (jump > 0) {
        stopped = false;
        noteUserActivity();
      }
      if (jump > (cardHeight + ADHA_UNITS_GRID_GAP) * FAST_SCROLL_CARD_FRACTION) {
        flinging = true;
      }
      // Fallback for browsers without `scrollend`: treat 100ms without events as stopped.
      if (settleTimer != null) {
        clearTimeout(settleTimer);
      }
      settleTimer = setTimeout(settle, FLING_SETTLE_MS);
      if (!flinging) {
        publish();
      }
      // While flinging, skeletons are already in place, so nothing renders until it stops.
    },
    setCardHeight: (height) => {
      if (height <= 0 || Math.abs(height - cardHeight) < CARD_HEIGHT_EPSILON) {
        return;
      }
      cardHeight = height;
      publish();
    },
  };
}

const FALLBACK_STORE = createListingViewportStore();

export const ListingViewportContext = createContext<ListingViewportStore>(FALLBACK_STORE);

export interface ViewportListingCards {
  /** Units shown as real cards. Grows as rows scroll into view; never shrinks on scroll. */
  readonly mappedUnitIds: ReadonlySet<string>;
  readonly listingImagesByUnitId: Readonly<Record<string, { uri: string }[]>>;
  /**
   * One number per block of LISTING_SLOT_BLOCK_SIZE slots. It changes only when
   * that block gains a mapped card or its photos arrive, so other blocks skip rendering.
   */
  readonly blockRevisions: readonly number[];
  /** Changes when the list is replaced (filter / project change). */
  readonly listEpoch: number;
  /** Measured card height. Skeleton slots use it so rows keep the same pitch. */
  readonly cardHeight: number;
  readonly onCardLayout: (height: number) => void;
}

/**
 * Every unit from the response gets a slot. Rows in the pane (plus one row of
 * overscan) are mapped to real cards; all other slots stay skeletons. A unit
 * that has been mapped stays a real card when it scrolls out and back.
 * During a fling nothing is mapped; when the pane stops, the cards in view are
 * the first thing resolved. After 5s without input, the remaining units are
 * mapped in the background from the top.
 */
export function useViewportListingCards<T extends { unit_id: string }>(
  units: readonly T[],
  columnCount: number,
  unitImageLookup: Readonly<Record<string, readonly PropertyImage[]>> | null,
  /**
   * Changes whenever the list is filtered differently. A new filter can keep the
   * same first unit and grow, which would otherwise look like an appended page.
   */
  listKey = '',
): ViewportListingCards {
  const store = useContext(ListingViewportContext);
  const snapshot = useTransitionSnapshot(store);
  const cacheRef = useRef(new Map<string, { uri: string }[]>());
  const mappedIdsRef = useRef(new Set<string>());
  const mappedPerBlockRef = useRef<number[]>([]);
  const backgroundCursorRef = useRef(0);
  const [backgroundTick, setBackgroundTick] = useState(0);
  const listEpochRef = useRef(0);
  const listIdentityRef = useRef({ firstId: '', length: 0, listKey });
  const firstId = units[0]?.unit_id ?? '';
  if (
    firstId !== listIdentityRef.current.firstId ||
    units.length < listIdentityRef.current.length ||
    listKey !== listIdentityRef.current.listKey
  ) {
    cacheRef.current = new Map();
    mappedIdsRef.current = new Set();
    mappedPerBlockRef.current = [];
    backgroundCursorRef.current = 0;
    listEpochRef.current += 1;
  }
  listIdentityRef.current = { firstId, length: units.length, listKey };

  // Idle for 5s: map the next unmapped units from the top, a few per step, as
  // background work. Any input pauses it; it resumes after the next 5s of idle.
  useEffect(() => {
    installActivityListeners();
    if (backgroundCursorRef.current >= units.length) {
      return undefined;
    }
    let timer: ReturnType<typeof setTimeout> | null = null;
    const step = () => {
      const wait = msUntilIdle();
      if (wait > 0) {
        timer = setTimeout(step, wait);
        return;
      }
      let added = 0;
      let cursor = backgroundCursorRef.current;
      while (cursor < units.length && added < BACKGROUND_BATCH_SIZE) {
        const unitId = units[cursor]?.unit_id;
        if (unitId != null && !mappedIdsRef.current.has(unitId)) {
          mappedIdsRef.current.add(unitId);
          const block = Math.floor(cursor / LISTING_SLOT_BLOCK_SIZE);
          mappedPerBlockRef.current[block] = (mappedPerBlockRef.current[block] ?? 0) + 1;
          added += 1;
        }
        cursor += 1;
      }
      backgroundCursorRef.current = cursor;
      if (added > 0) {
        startTransition(() => setBackgroundTick((tick) => tick + 1));
      }
    };
    timer = setTimeout(step, msUntilIdle());
    return () => {
      if (timer != null) {
        clearTimeout(timer);
      }
    };
  }, [units, backgroundTick]);

  const columns = Math.max(1, columnCount);
  const cardHeight = snapshot.cardHeight > 0 ? snapshot.cardHeight : ESTIMATED_CARD_HEIGHT;
  const stride = cardHeight + ADHA_UNITS_GRID_GAP;
  const viewportHeight =
    snapshot.viewportHeight > 0 ? snapshot.viewportHeight : FALLBACK_VIEWPORT_HEIGHT;
  const totalRows = Math.ceil(units.length / columns);
  const firstRowInView = Math.max(
    0,
    Math.floor(Math.max(0, snapshot.scrollY) / stride) - OVERSCAN_ROWS,
  );
  const rowsInView = Math.ceil(viewportHeight / stride) + OVERSCAN_ROWS * 2;
  const lastRowInView = Math.min(totalRows, firstRowInView + rowsInView);
  const inViewStart = firstRowInView * columns;
  const inViewEnd = Math.min(units.length, lastRowInView * columns);

  // A unit in view becomes a real card even before Contentful answers; its
  // photos are filled in once the lookup exists.
  const mappedPerBlock = mappedPerBlockRef.current;
  for (let index = inViewStart; index < inViewEnd; index += 1) {
    const unitId = units[index]?.unit_id;
    if (unitId == null || mappedIdsRef.current.has(unitId)) {
      continue;
    }
    mappedIdsRef.current.add(unitId);
    const block = Math.floor(index / LISTING_SLOT_BLOCK_SIZE);
    mappedPerBlock[block] = (mappedPerBlock[block] ?? 0) + 1;
  }
  if (unitImageLookup) {
    for (const unit of units) {
      if (!mappedIdsRef.current.has(unit.unit_id) || cacheRef.current.has(unit.unit_id)) {
        continue;
      }
      cacheRef.current.set(
        unit.unit_id,
        resolveListingCardImages(
          unitImageLookup,
          readListingUnitModel(unit),
          unit.unit_id,
        ),
      );
    }
  }

  const listingImagesByUnitId = imagesForMapped(cacheRef.current);

  const onCardLayout = useCallback(
    (height: number) => {
      store.setCardHeight(height);
    },
    [store],
  );

  const blockCount = Math.ceil(units.length / LISTING_SLOT_BLOCK_SIZE);
  const photosFlag = unitImageLookup ? 1 : 0;
  const blockRevisions: number[] = [];
  for (let block = 0; block < blockCount; block += 1) {
    blockRevisions.push((mappedPerBlock[block] ?? 0) * 2 + photosFlag);
  }

  return {
    mappedUnitIds: mappedIdsRef.current,
    listingImagesByUnitId,
    blockRevisions,
    listEpoch: listEpochRef.current,
    cardHeight,
    onCardLayout,
  };
}

/**
 * While the pane is moving, viewport changes render as a transition: mounting
 * real cards costs hundreds of ms, and React splits a transition into small
 * slices so scrolling keeps painting skeletons. Once the pane has stopped, the
 * cards in view render immediately at normal priority.
 */
function useTransitionSnapshot(store: ListingViewportStore): ListingViewportSnapshot {
  const [snapshot, setSnapshot] = useState(store.getSnapshot);
  useEffect(() => {
    const sync = () => {
      if (store.isStopped()) {
        setSnapshot(store.getSnapshot());
        return;
      }
      startTransition(() => {
        setSnapshot(store.getSnapshot());
      });
    };
    sync();
    return store.subscribe(sync);
  }, [store]);
  return snapshot;
}

function imagesForMapped(
  cache: Map<string, { uri: string }[]>,
): Record<string, { uri: string }[]> {
  if (cache.size === 0) {
    return EMPTY_IMAGES;
  }
  return Object.fromEntries(cache);
}
